import httpClient from "../http/http-client";
import { ILibraryRuleDeleteResult } from '../library-rule/library-rule.model';
import {
  ILibraryAnalyticReport,
  ILibraryAnalyticReportPushResult,
  ILibraryAnalyticReportPushTarget,
  ILibraryReportPeriodNames,
  ILibraryRetractResult,
  IPostLibraryAnalyticReport,
} from './library-analytic-report.model';

export default class LibraryAnalyticReportService {
  http;

  constructor() {
    this.http = httpClient
  }

  async getAllWithPagination(page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc', nameIncludes?: string): Promise<{ count: number, rows: ILibraryAnalyticReport[] }> {
    const params = new URLSearchParams({ page: String(page), perPage: String(perPage) });
    if (order && orderBy) { params.append('orderBy', orderBy); params.append('order', order); }
    if (nameIncludes) params.append('nameIncludes', nameIncludes);
    return this.http.get(`partner/library/analytic-reports?${params.toString()}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getOne(libraryAnalyticReportId: string): Promise<ILibraryAnalyticReport> {
    return this.http.get(`partner/library/analytic-reports/${libraryAnalyticReportId}?includeFilters=true`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async create(report: IPostLibraryAnalyticReport): Promise<ILibraryAnalyticReport> {
    return this.http.post(`partner/library/analytic-reports`, report)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async edit(libraryAnalyticReportId: string, report: IPostLibraryAnalyticReport): Promise<ILibraryAnalyticReport> {
    return this.http.patch(`partner/library/analytic-reports/${libraryAnalyticReportId}`, report)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  // removeFromClients also takes the report back out of the client accounts it reached.
  // A copy a client has wired into a scheduled send can't be deleted without breaking that
  // schedule, so those are released to the client instead and reported.
  async delete(libraryAnalyticReportId: string, removeFromClients: boolean = false): Promise<ILibraryRuleDeleteResult> {
    return this.http.delete(`partner/library/analytic-reports/${libraryAnalyticReportId}`, { data: { removeFromClients } })
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getPushTargets(libraryAnalyticReportId: string): Promise<{ clients: ILibraryAnalyticReportPushTarget[] }> {
    return this.http.get(`partner/library/analytic-reports/${libraryAnalyticReportId}/targets`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async push(libraryAnalyticReportId: string, clientOrganisationIds: string[], overwriteClientEditIds: string[] = []): Promise<ILibraryAnalyticReportPushResult> {
    return this.http.post(`partner/library/analytic-reports/${libraryAnalyticReportId}/push`, { clientOrganisationIds, overwriteClientEditIds })
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async retract(libraryAnalyticReportId: string, clientOrganisationIds: string[]): Promise<ILibraryRetractResult> {
    return this.http.post(`partner/library/analytic-reports/${libraryAnalyticReportId}/retract`, { clientOrganisationIds })
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async setClientEditPermission(libraryAnalyticReportId: string, allowClientEdit: boolean): Promise<ILibraryAnalyticReport> {
    return this.http.patch(`partner/library/analytic-reports/${libraryAnalyticReportId}/client-edit`, { allowClientEdit })
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  // Distinct report-period names across this partner's clients, with how many hold each.
  async getReportPeriodNames(): Promise<ILibraryReportPeriodNames> {
    return this.http.get(`partner/library/analytic-reports/report-period-names`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }
}
